import React, { useEffect, useState } from 'react';
import { Button, Form, Input, message, Upload, Tag } from 'antd';
import { useHistory } from 'react-router-dom';
import { UploadOutlined, PlusOutlined, CloseOutlined, EnvironmentOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import routes from 'routes';
import { useMutation, useQuery } from 'react-query';
import { updateIntermediaryProfile, fetchIntermediaryProfile, upload_api } from 'services/apiSrv';
import UIOutlet from 'components/common/UIOutlet';

const FormContainer = styled.div`
    max-width: 100%;
`;

const PageHeader = styled.div`
    margin-bottom: 24px;
`;

const Title = styled.h1`
    font-size: 20px;
    font-weight: 600;
    color: #001F5C;
    margin: 0 0 6px 0;
    letter-spacing: -0.02em;
`;

const Divider = styled.div`
    width: 40px;
    height: 3px;
    background: #EF3340;
    border-radius: 2px;
`;

const FormSection = styled.div`
    margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
    font-size: 12px;
    font-weight: 600;
    color: #001F5C;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 12px 0;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 16px;
`;

const ProfileTypeCards = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-top: 8px;
`;

const ProfileCard = styled.div`
    padding: 16px;
    border: 2px solid ${props => props.selected ? '#001F5C' : '#e8e8e8'};
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${props => props.selected ? '#F8F9FF' : '#ffffff'};
    text-align: center;

    &:hover {
        border-color: ${props => props.selected ? '#001F5C' : '#00B8D4'};
        background: ${props => props.selected ? '#F8F9FF' : '#FAFBFF'};
    }
`;

const CardIcon = styled.div`
    font-size: 24px;
    margin-bottom: 8px;
    color: ${props => props.selected ? '#001F5C' : '#8c8c8c'};
`;

const CardTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${props => props.selected ? '#001F5C' : '#262626'};
    margin-bottom: 2px;
`;

const CardDescription = styled.div`
    font-size: 12px;
    color: #8c8c8c;
`;

const EmailInputWrapper = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
`;

const EmailList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 10px;
    background: #fafafa;
    border-radius: 6px;
    min-height: 42px;
    border: 1px solid #e8e8e8;
`;

const EmailTag = styled(Tag)`
    margin: 0;
    padding: 4px 10px;
    font-size: 13px;
    border-radius: 4px;
    background: #ffffff;
    border: 1px solid #d9d9d9;
`;

const LocationInput = styled.div`
    position: relative;

    .ant-input-prefix {
        color: #00B8D4;
    }
`;

const StyledFormItem = styled(Form.Item)`
    margin-bottom: 0;

    .ant-form-item-label > label {
        font-size: 12px;
        font-weight: 500;
        color: #595959;
        height: 20px;
    }

    .ant-input,
    .ant-input-textarea textarea {
        border-radius: 6px;
        border-color: #e8e8e8;

        &:focus,
        &:hover {
            border-color: #00B8D4;
        }
    }
`;

const ActionButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
`;

const SubmitButton = styled(Button)`
    background: #001F5C;
    border-color: #001F5C;
    height: 36px;
    padding: 0 24px;
    font-weight: 500;
    font-size: 14px;

    &:hover,
    &:focus {
        background: #00306B;
        border-color: #00306B;
    }
`;

const initialProfileData = {
    intermediaryLocation: [-3.0007, 76.5000],
    kraPin: 'AS677788888',
    profileImage: 'http://test.image',
    profileType: 'COMPANY',
    contacts: ['tr@ymail.com', 'kk@ymail.com'],
    description: 'Intermediary Description',
    intermediaryNames: 'Paparazi Intermediary',
};

// File upload function
const uploadProductImage = async (options) => {
    const { onSuccess, onError, file } = options;

    try {
        let res = await upload_api("profileimage", file);
        console.log("Profile Image===:", res);
        onSuccess(res.data[0].fileUrl);
        message.success(`${file.name} uploaded successfully.`);
        return res.data[0].fileUrl;
    } catch (err) {
        console.error("File upload error:", err);
        onError(err);
        message.error(`${file.name} upload failed.`);
    }
};

function ProfileSetup() {
    const [form] = Form.useForm();
    const history = useHistory();
    const [emailInput, setEmailInput] = useState('');
    const [emails, setEmails] = useState([]);
    const [selectedProfileType, setSelectedProfileType] = useState('COMPANY');

    // Fetch initial profile data
    const { data: profileData, isLoading } = useQuery('profile', fetchIntermediaryProfile, {
        initialData: initialProfileData,
        onError: (error) => {
            message.error('Failed to fetch profile data');
            console.error('Fetch error:', error);
        },
    });

    // Mutation for updating profile
    const { mutate, isLoading: isUpdating } = useMutation(updateIntermediaryProfile, {
        onSuccess: () => {
            message.success('Profile updated successfully');
            history.push(routes.kycsetup);
        },
        onError: (error) => {
            message.error('Failed to update profile');
            console.error('Update error:', error);
        },
    });

    // Sync form with fetched profile data
    useEffect(() => {
        if (profileData) {
            form.setFieldsValue({
                intermediaryNames: profileData.intermediaryNames,
                kraPin: profileData.kraPin,
                description: profileData.description,
                profileImage: profileData.profileImage ? [{ url: profileData.profileImage, name: 'Profile Image', status: 'done' }] : [],
                intermediaryLocation: profileData?.intermediaryLocation?.join(', '),
            });
            setSelectedProfileType(profileData.profileType);
            setEmails(profileData?.contacts || []);
        }
    }, [profileData, form]);

    const addEmail = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailInput.trim()) {
            message.warning('Please enter an email address');
            return;
        }
        if (!emailRegex.test(emailInput.trim())) {
            message.error('Please enter a valid email address');
            return;
        }
        if (emails.includes(emailInput.trim())) {
            message.warning('This email is already added');
            return;
        }
        setEmails([...emails, emailInput.trim()]);
        setEmailInput('');
    };

    const removeEmail = (emailToRemove) => {
        setEmails(emails.filter(email => email !== emailToRemove));
    };

    const onFinish = async (values) => {
        if (emails.length === 0) {
            message.error('Please add at least one contact email');
            return;
        }

        const updatedProfile = {
            intermediaryNames: values.intermediaryNames,
            kraPin: values.kraPin,
            description: values.description,
            contacts: emails,
            profileImage: values.profileImage?.[0]?.url || profileData.profileImage,
            intermediaryLocation: [], //values.intermediaryLocation.split(',').map((coord) => parseFloat(coord.trim())),
            profileType: selectedProfileType,
        };
        mutate(updatedProfile);
    };

    const onFinishFailed = (errorInfo) => {
        console.log('Form validation failed:', errorInfo);
    };

    // Custom upload props
    const uploadProps = {
        name: 'file',
        customRequest: uploadProductImage,
        maxCount: 1,
        accept: 'image/*',
        onChange: (info) => {
            if (info.file.status === 'done') {
                form.setFieldsValue({
                    profileImage: [{ url: info.file.response, name: info.file.name, status: 'done' }],
                });
            }
        },
    };

    if (isLoading) return <div>Loading...</div>;

    return (
        <UIOutlet
            title="Profile Setup"
            logoSrc="https://res.cloudinary.com/doifxgf1h/image/upload/v1768298670/Nexus_Versions_FA-01-removebg-preview_mcak7j.png"
            bannerTitle="Complete Your Profile"
            bannerDescription="Set up your intermediary profile to get started on the platform"
        >
            <FormContainer>
                <PageHeader>
                    <Title>Profile Information</Title>
                    <Divider />
                </PageHeader>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <FormSection>
                        <SectionTitle>Basic Information</SectionTitle>
                        <FormGrid>
                            <StyledFormItem
                                label="Intermediary Name"
                                name="intermediaryNames"
                                rules={[{ required: true, message: 'Intermediary name is required' }]}
                            >
                                <Input placeholder="Enter your business name" />
                            </StyledFormItem>

                            <StyledFormItem
                                label="KRA PIN"
                                name="kraPin"
                                rules={[{ required: true, message: 'KRA PIN is required' }]}
                            >
                                <Input placeholder="Enter your KRA PIN" />
                            </StyledFormItem>

                            <div style={{ gridColumn: '1 / -1' }}>
                                <StyledFormItem
                                    label="Description"
                                    name="description"
                                    rules={[{ required: true, message: 'Description is required' }]}
                                >
                                    <Input.TextArea
                                        rows={3}
                                        placeholder="Tell us about your business"
                                        style={{ borderRadius: '6px' }}
                                    />
                                </StyledFormItem>
                            </div>

                            <div style={{ gridColumn: '1 / -1' }}>
                                <SectionTitle style={{ marginTop: '8px' }}>Profile Type</SectionTitle>
                                <ProfileTypeCards>
                                    <ProfileCard
                                        selected={selectedProfileType === 'COMPANY'}
                                        onClick={() => setSelectedProfileType('COMPANY')}
                                    >
                                        <CardIcon selected={selectedProfileType === 'COMPANY'}>🏢</CardIcon>
                                        <CardTitle selected={selectedProfileType === 'COMPANY'}>Company</CardTitle>
                                        <CardDescription>Registered business entity</CardDescription>
                                    </ProfileCard>

                                    <ProfileCard
                                        selected={selectedProfileType === 'INDIVIDUAL'}
                                        onClick={() => setSelectedProfileType('INDIVIDUAL')}
                                    >
                                        <CardIcon selected={selectedProfileType === 'INDIVIDUAL'}>👤</CardIcon>
                                        <CardTitle selected={selectedProfileType === 'INDIVIDUAL'}>Individual</CardTitle>
                                        <CardDescription>Personal account</CardDescription>
                                    </ProfileCard>
                                </ProfileTypeCards>
                            </div>
                        </FormGrid>
                    </FormSection>

                    <FormSection>
                        <SectionTitle>Contact Information</SectionTitle>
                        <EmailInputWrapper>
                            <Input
                                placeholder="Enter email address"
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                onPressEnter={addEmail}
                                style={{ borderRadius: '6px' }}
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={addEmail}
                                style={{ background: '#00B8D4', borderColor: '#00B8D4', height: '32px' }}
                            >
                                Add
                            </Button>
                        </EmailInputWrapper>

                        {emails.length > 0 && (
                            <EmailList>
                                {emails.map((email, index) => (
                                    <EmailTag
                                        key={index}
                                        closable
                                        closeIcon={<CloseOutlined />}
                                        onClose={() => removeEmail(email)}
                                    >
                                        {email}
                                    </EmailTag>
                                ))}
                            </EmailList>
                        )}
                    </FormSection>

                    {/* <FormSection>
            <SectionTitle>Location</SectionTitle>
            <LocationInput>
              <StyledFormItem
                label="Intermediary Location (latitude, longitude)"
                name="intermediaryLocation"
                rules={[
                  { required: true, message: 'Location coordinates are required' },
                  {
                    validator: (_, value) => {
                      const coords = value.split(',').map((coord) => parseFloat(coord.trim()));
                      if (
                        coords.length === 2 &&
                        coords.every((coord) => !isNaN(coord)) &&
                        coords[0] >= -90 &&
                        coords[0] <= 90 &&
                        coords[1] >= -180 &&
                        coords[1] <= 180
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject('Please enter valid coordinates (latitude: -90 to 90, longitude: -180 to 180)');
                    },
                  },
                ]}
              >
                <Input
                  prefix={<EnvironmentOutlined />}
                  placeholder="e.g., -3.0007, 76.5000"
                />
              </StyledFormItem>
            </LocationInput>
          </FormSection> */}

                    <FormSection>
                        <SectionTitle>Profile Image</SectionTitle>
                        <StyledFormItem
                            name="profileImage"
                            rules={[{ required: true, message: 'Profile image is required' }]}
                            valuePropName="fileList"
                            getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
                        >
                            <Upload {...uploadProps} listType="picture-card">
                                <div>
                                    <UploadOutlined style={{ fontSize: '20px', color: '#00B8D4' }} />
                                    <div style={{ marginTop: 6, fontSize: '12px' }}>Upload</div>
                                </div>
                            </Upload>
                        </StyledFormItem>
                    </FormSection>

                    <ActionButtons>
                        <SubmitButton
                            type="primary"
                            htmlType="submit"
                            loading={isUpdating}
                        >
                            Continue to KYC Setup
                        </SubmitButton>
                    </ActionButtons>
                </Form>
            </FormContainer>
        </UIOutlet>
    );
}

export default ProfileSetup;